/**
 * TunePage.js
 * Metadata-driven renderer for tune-notation pages.
 * Replaces the baked-HTML approach (dangerouslySetInnerHTML of a full CSS+HTML snapshot).
 * All structured data comes from pageData.metadata.asset_ids + three lean API calls.
 *
 * Rendered by PageViewer when pageType === 'tune-notation' || 'abc-notation'.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { decodeHtmlEntities } from '../utils/htmlDecode';
import { resolveCuratedItemsForViewer } from '../utils/resolveCuratedItems';
import {
  collectTuneAudioIds,
  extractTuneNotationExternalImageUrlFromHtml,
  tuneNotationLegacyImageUrl,
} from '../utils/tunePageMedia';
import { normalizeTuneSourceUrl } from '../utils/normalizeTuneSourceUrl';
import { formatAlternateTitles } from '../utils/tuneAlternateTitles';
import NotationAssetImg from './NotationAssetImg';
import UserContentFeedback from './UserContentFeedback';
import TuneFormatMarker from './TuneFormatMarker';
import {
  fetchTuneSiteOptions,
  filterRelatedTunesByScore,
  DEFAULT_RELATED_TUNES_MIN_SCORE,
} from '../utils/tuneSiteOptions';
import './TunePage.css';

const FINE_PRINT =
  "Many of the tunes posted on this site are Paul Cranford's initial drafts. " +
  'While many have already been featured in his published collections, others are ' +
  'currently being refined for future release through Cranford Publications.';

/* ── helpers ────────────────────────────────────────────────────────── */
function parseMeta(raw) {
  if (!raw) return {};
  try { return typeof raw === 'string' ? JSON.parse(raw) : raw; }
  catch (_) { return {}; }
}

function getTunesSectionMode(meta) {
  const sect = (meta.related_content && meta.related_content.sections) || {};
  const v = sect.tunes;
  if (v && typeof v === 'object' && v.mode != null) return String(v.mode);
  if (typeof v === 'string' && v.trim()) return v.trim();
  return 'tags';
}

function formatComposerDisplay(c) {
  if (c == null || c === '') return '';
  if (Array.isArray(c)) return c.map((x) => String(x).trim()).filter(Boolean).join(', ');
  return String(c).trim();
}

function rowToRelatedTuneRow(row) {
  const meta = row.metadata || {};
  let tags = meta.tags;
  if (Array.isArray(tags)) tags = tags.join(', ');
  else if (tags != null) tags = String(tags);
  else tags = '';
  return {
    page_id: row.page_id,
    title: row.title,
    region: meta.region,
    tune_type: meta.tune_type,
    album: meta.album || meta.recording_album,
    composer: formatComposerDisplay(meta.composer),
    tags: tags || '',
    score: row.score ?? null,
    type: row.type || 'tune-notation',
    asset_id: row.asset_id,
    has_audio: row.has_audio,
    has_notation: row.has_notation,
    metadata: meta,
  };
}

/** Load metadata for fuzzy related-tune rows so format markers can resolve audio/notation. */
async function enrichRelatedTuneRows(rows) {
  const list = (rows || []).filter((t) => t && t.page_id);
  if (!list.length) return [];

  const needIds = [
    ...new Set(
      list.filter((t) => !t.metadata || typeof t.metadata !== 'object').map((t) => t.page_id)
    ),
  ];

  let metaById = new Map();
  if (needIds.length) {
    const pages = await fetch(
      `/api/content/pages-by-ids?ids=${encodeURIComponent(needIds.join(','))}`,
      { cache: 'no-store' }
    ).then((r) => (r.ok ? r.json() : []));
    metaById = new Map((pages || []).map((p) => [p.page_id, p.metadata || {}]));
  }

  return list.map((t) =>
    rowToRelatedTuneRow({
      ...t,
      metadata:
        t.metadata && typeof t.metadata === 'object'
          ? t.metadata
          : metaById.get(t.page_id) || {},
    })
  );
}

/** First absolute http(s) URL among legacy keys (matches tunePageLayout / RelatedContentPreview). */
function resolveTuneOriginalPageUrl(meta) {
  if (!meta || typeof meta !== 'object') return '';
  for (const k of ['source_url', 'source', 'source_reference']) {
    const v = meta[k];
    const t = normalizeTuneSourceUrl(v != null ? String(v).trim() : '');
    if (/^https?:\/\//i.test(t)) return t;
  }
  return '';
}

function tuneMetaFromRow(metaRaw, pageHtml) {
  const m = parseMeta(metaRaw);
  const meta = m && typeof m === 'object' ? { ...m } : {};

  // Use ONLY asset_ids.images from metadata — never collect from legacy fields or baked HTML.
  const rawAI = meta.asset_ids;
  const notationIds = [];
  const _seen = new Set();
  const _addId = (v) => {
    const n = parseInt(String(v), 10);
    if (Number.isFinite(n) && n > 0 && !_seen.has(n)) { _seen.add(n); notationIds.push(n); }
  };
  if (Array.isArray(rawAI)) {
    rawAI.forEach(_addId);
  } else if (rawAI && typeof rawAI === 'object' && Array.isArray(rawAI.images)) {
    rawAI.images.forEach(_addId);
  }

  // Legacy URL — only used when no asset_ids.images are present at all.
  let notationLegacyUrl = notationIds.length === 0 ? tuneNotationLegacyImageUrl(meta) : '';
  if (!notationLegacyUrl && notationIds.length === 0) {
    notationLegacyUrl = extractTuneNotationExternalImageUrlFromHtml(pageHtml || '');
  }
  return {
    notationIds,
    notationLegacyUrl,
    audioIds: collectTuneAudioIds(meta),
    sourceUrl: resolveTuneOriginalPageUrl(meta),
    tuneType: meta.tune_type || '',
    keySignature: meta.key_signature || meta.key || '',
    timeSig: meta.time_signature || '',
    region: meta.region || '',
    artist: meta.artist || '',
    /** Canonical `recording_album`; `album` is legacy duplicate set by staging sync. */
    album: meta.recording_album || meta.album || '',
    book: meta.collection_book || '',
    alternateTitles: formatAlternateTitles(meta),
    tags:
      typeof meta.tags === 'string'
        ? meta.tags.split(',').map((t) => t.trim()).filter(Boolean)
        : Array.isArray(meta.tags)
          ? meta.tags
          : [],
    sectionLimits: (meta.related_content && meta.related_content.section_limits) || {},
    sectionTitles: (meta.related_content && meta.related_content.section_titles) || {},
    ttaUrl: meta.tta_url || '',
    cbfiddleUrl: meta.cbfiddle_url || '',
  };
}

/** Mirrors GET /api/content/tune-sections when the network call fails. */
function buildTuneSectionsFallback(metadata) {
  const m = parseMeta(metadata);
  const rc = m.related_content || {};
  const sects = rc.sections || {};
  const rawLim = rc.section_limits || {};
  const clamp = (v, def) => {
    const n = parseInt(v, 10);
    if (!Number.isFinite(n)) return def;
    return Math.max(1, Math.min(100, n));
  };
  return {
    sections: {
      products: sects.products || 'explicit',
      articles: sects.articles || 'off',
      tunes: getTunesSectionMode(m),
      curated: sects.curated || 'off',
    },
    section_limits: {
      products: clamp(rawLim.products, 24),
      articles: clamp(rawLim.articles, 8),
      tunes: clamp(rawLim.tunes, 12),
      curated: clamp(rawLim.curated, 24),
    },
    pageTags:
      typeof m.tags === 'string' ? m.tags : (Array.isArray(m.tags) ? m.tags.join(',') : ''),
    curatedItems: Array.isArray(rc.curated) ? rc.curated : [],
  };
}

function normalizeArticleRow(a) {
  const page_id = a.page_id || a.pageId;
  if (!page_id) return null;
  return {
    page_id: String(page_id),
    title: a.title || String(page_id),
    description: a.description != null ? String(a.description) : '',
    page_type: a.page_type || a.pageType || 'article',
  };
}

/* ── sub-components ─────────────────────────────────────────────────── */

function ProductCard({ product, onClick }) {
  const [imgError, setImgError] = useState(false);
  return (
    <div className="related-product-card related-product-card--cover-only" onClick={onClick}>
      <a
        href={`/product/${product.id}`}
        className="related-product-link related-product-link--cover-only"
        title={product.name}
        aria-label={`View product: ${product.name}`}
        onClick={e => e.preventDefault()}
      >
        {product.image && !imgError ? (
          <div className="related-product-image">
            <img
              src={product.image}
              alt=""
              loading="lazy"
              onError={() => setImgError(true)}
            />
          </div>
        ) : (
          <div className="related-product-image related-product-image--empty" aria-hidden="true">
            <span className="related-product-fallback">📚</span>
          </div>
        )}
      </a>
    </div>
  );
}

function TuneCard({ tune, onClick }) {
  const region   = tune.region    || 'Traditional';
  const tuneType = tune.tune_type || 'Tune';
  const album    = tune.album     || '';
  const composer = formatComposerDisplay(tune.composer);
  const tagsArr  = typeof tune.tags === 'string'
    ? tune.tags.split(',').map(t => t.trim()).filter(Boolean)
    : (Array.isArray(tune.tags) ? tune.tags.map(String) : []);

  return (
    <a
      href={`/page/${tune.page_id}`}
      className="related-tune-card related-tune-card--marked"
      onClick={e => { e.preventDefault(); onClick(); }}
    >
      <div className="tune-card-header">
        <h3 className="tune-card-title">{tune.title}</h3>
        <TuneFormatMarker item={tune} variant="inline" />
        {tune.score && tune.score < 100 && (
          <span className="tune-card-score">{tune.score.toFixed(1)}</span>
        )}
      </div>
      {composer ? <p className="tune-card-composer">{composer}</p> : null}
      <p className="tune-card-meta">{region} • {tuneType}</p>
      {album && <p className="tune-card-album">💿 {album}</p>}
      {tagsArr.length > 0 && (
        <div className="tune-card-tags">
          {tagsArr.slice(0, 4).map(tag => (
            <span key={tag} className="tune-card-tag">{tag}</span>
          ))}
        </div>
      )}
    </a>
  );
}

function ArticleCard({ article, onClick }) {
  return (
    <div className="related-article-card">
      <a
        href={`/page/${article.page_id}`}
        className="related-article-link"
        onClick={e => { e.preventDefault(); onClick(); }}
      >
        <div className="related-article-title">{article.title}</div>
        {article.description && (
          <div className="related-article-desc">
            {article.description.length > 120
              ? article.description.slice(0, 120) + '…'
              : article.description}
          </div>
        )}
        <span className="related-article-type">{article.page_type || 'article'}</span>
      </a>
    </div>
  );
}

const CURATED_ICONS = {
  tune: '🎵',
  article: '📰',
  product: '📚',
  route: '🔗',
  'tune-asset': '🎼',
};
const CURATED_LABELS = {
  tune: 'Tune',
  article: 'Page',
  product: 'Book / Recording',
  route: 'Link',
  'tune-asset': 'ABC',
};

function curatedCardKind(item) {
  if (item.itemType === 'product') return 'product';
  if (item.itemType === 'route') return 'route';
  if (item.itemType === 'tune-asset') return 'tune-asset';
  if (item.itemType === 'tune') return 'tune';
  return 'article';
}

function CuratedCard({ item, onNavigate }) {
  const kind = curatedCardKind(item);
  const icon = CURATED_ICONS[kind] || '🔗';
  const label = CURATED_LABELS[kind] || kind;

  let displayTitle = item.title || item.name || '';
  if (kind === 'product') displayTitle = item.name || `Product #${item.id}`;
  else if (kind === 'route') displayTitle = item.title || item.routeHref || '';
  else if (kind === 'tune-asset') displayTitle = item.title || `ABC #${item.tuneAssetId ?? item.id}`;
  else if (!displayTitle && item.page_id) displayTitle = item.page_id;

  const desc =
    kind !== 'product' && kind !== 'route' && kind !== 'tune-asset'
      ? String(item.description || '').slice(0, 120)
      : '';

  const href =
    kind === 'product'
      ? `/product/${item.id}`
      : kind === 'tune-asset'
        ? `/tune-asset/${item.tuneAssetId ?? item.id}`
        : kind === 'route'
          ? item.routeHref || '/'
          : `/page/${item.page_id}`;

  const go = (e) => {
    e.preventDefault();
    if (kind === 'product') onNavigate(`/product/${item.id}`);
    else if (kind === 'tune-asset') onNavigate(`/tune-asset/${item.tuneAssetId ?? item.id}`);
    else if (kind === 'route') onNavigate(item.routeHref);
    else onNavigate(`/page/${item.page_id}`);
  };

  return (
    <div className="curated-card">
      <a href={href} onClick={go}>
        <span className="curated-card-icon">{icon}</span>
        <div className="curated-card-body">
          <div className="curated-card-title">{displayTitle}</div>
          {desc ? <div className="curated-card-desc">{desc}</div> : null}
        </div>
        <span className="curated-card-type">{label}</span>
      </a>
    </div>
  );
}

/* ── main component ─────────────────────────────────────────────────── */

export default function TunePage({ pageData, pageId }) {
  const navigate = useNavigate();
  const { currentUser, hasAnyRole } = useAuth();
  const isAdmin = !!(
    (currentUser?.roles && ['admin', 'super_admin', 'content_creator'].some(r => currentUser.roles.includes(r))) ||
    (hasAnyRole && hasAnyRole(['admin', 'super_admin', 'content_creator']))
  );

  const [products, setProducts] = useState(null);
  const [tunes, setTunes] = useState(null);
  const [articles, setArticles] = useState(null);
  const [curated, setCurated] = useState(null);
  const [cranfordNotationFallback, setCranfordNotationFallback] = useState('');

  const tuneMeta = tuneMetaFromRow(pageData?.metadata, pageData?.html);
  const title       = decodeHtmlEntities(pageData?.title || '');
  const description = pageData?.description || '';

  const sectionTitle = (key, fallback) =>
    tuneMeta.sectionTitles[key] || fallback;

  const cap = (key, def) => {
    const n = parseInt(tuneMeta.sectionLimits[key], 10);
    return Number.isFinite(n) && n >= 1 ? Math.min(100, n) : def;
  };

  const tuneLimit    = cap('tunes', 12);
  const productLimit = cap('products', 24);
  const articleLimit = cap('articles', 8);
  const curatedLimit = cap('curated', 24);

  const notationFallbackUrl =
    tuneMeta.notationLegacyUrl || cranfordNotationFallback;

  const cranfordSourceUrlForScrape = useMemo(() => {
    const m = parseMeta(pageData?.metadata);
    const candidates = [m.source_url, m.source, m.source_reference].map((u) =>
      normalizeTuneSourceUrl(u != null ? String(u).trim() : '')
    );
    const raw = candidates.find(
      (n) => n && /^https?:\/\//i.test(n) && /cranfordpub\.com/i.test(n)
    );
    return raw || '';
  }, [pageData?.metadata]);

  useEffect(() => {
    setCranfordNotationFallback('');
    if (!cranfordSourceUrlForScrape) return undefined;
    if (tuneMeta.notationLegacyUrl) return undefined;
    let cancelled = false;
    fetch(
      `/api/content/notation-from-source?url=${encodeURIComponent(cranfordSourceUrlForScrape)}`
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!cancelled && j && typeof j.url === 'string' && j.url) {
          setCranfordNotationFallback(j.url);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [pageId, cranfordSourceUrlForScrape, tuneMeta.notationLegacyUrl]);

  // Re-run when metadata / section mode / explicit picks / caps change.
  const relatedDataKey = useMemo(() => {
    const m = parseMeta(pageData?.metadata);
    const rc = m.related_content || {};
    const sect = rc.sections || {};
    const tagsRaw = m.tags;
    const tagsStr = Array.isArray(tagsRaw)
      ? tagsRaw.join(',')
      : (typeof tagsRaw === 'string' ? tagsRaw : '');
    const explicitIds = JSON.stringify((rc.tunes || []).map((t) => t.id).filter(Boolean));
    const mode = getTunesSectionMode(m);
    return [
      tagsStr,
      m.region || '',
      m.tune_type || '',
      String(m.artist || ''),
      String(m.album || m.recording_album || ''),
      String(m.collection_book || ''),
      explicitIds,
      mode,
      sect.articles || '',
      sect.products || '',
      sect.curated || '',
      JSON.stringify(rc.section_limits || {}),
      JSON.stringify(rc.curated || []),
    ].join('\u0001');
  }, [pageData?.metadata]);

  useEffect(() => {
    if (!pageId) return;
    let cancelled = false;

    (async () => {
      let cfg = buildTuneSectionsFallback(pageData?.metadata);
      try {
        const tsRes = await fetch(
          `/api/content/tune-sections/${encodeURIComponent(pageId)}`,
          { cache: 'no-store' }
        );
        if (tsRes.ok) {
          const j = await tsRes.json();
          if (!cancelled && j) cfg = j;
        }
      } catch (_) {
        /* use fallback cfg */
      }
      if (cancelled) return;

      const sect = cfg.sections || {};
      const lim = cfg.section_limits || {};
      const limNum = (k, def) => {
        const n = parseInt(lim[k], 10);
        return Number.isFinite(n) && n >= 1 ? Math.min(100, n) : def;
      };
      const tLim = limNum('tunes', 12);
      const aLim = limNum('articles', 8);
      const artFetchLim = Math.min(100, Math.max(aLim, 8));
      const tunesMode = sect.tunes || 'tags';
      const metaFull = parseMeta(pageData?.metadata);
      const rc = metaFull.related_content || {};
      const explicitTuneIds = (rc.tunes || []).map((t) => t.id).filter(Boolean);
      const pageTags = cfg.pageTags || '';

      const productsPromise =
        sect.products === 'off'
          ? Promise.resolve([])
          : fetch(`/api/content/tunes/${pageId}/products`, { cache: 'no-store' })
              .then((r) => (r.ok ? r.json() : []))
              .catch(() => []);

      const articleFetches = [];
      if (sect.articles === 'explicit' || sect.articles === 'both') {
        articleFetches.push(
          fetch(`/api/content/tunes/${pageId}/articles`, { cache: 'no-store' })
            .then((r) => (r.ok ? r.json() : []))
            .catch(() => [])
        );
      }
      if (
        (sect.articles === 'tags' || sect.articles === 'both') &&
        String(pageTags).trim()
      ) {
        articleFetches.push(
          fetch(
            `/api/content/related?tags=${encodeURIComponent(pageTags)}&limit=${artFetchLim}&exclude=${encodeURIComponent(pageId)}&content_type=article`,
            { cache: 'no-store' }
          )
            .then((r) => (r.ok ? r.json() : []))
            .catch(() => [])
        );
      }

      try {
        const [prods, ...artParts] = await Promise.all([
          productsPromise,
          ...articleFetches,
        ]);
        if (cancelled) return;

        const mergedArts = [];
        const seenArt = new Set();
        for (const arr of artParts) {
          for (const raw of arr || []) {
            const n = normalizeArticleRow(raw);
            if (n && !seenArt.has(n.page_id)) {
              seenArt.add(n.page_id);
              mergedArts.push(n);
            }
          }
        }
        setProducts(Array.isArray(prods) ? prods : []);
        setArticles(mergedArts);

        let tuneRows = [];
        if (tunesMode === 'off') {
          tuneRows = [];
        } else if (tunesMode === 'explicit') {
          if (explicitTuneIds.length) {
            const pages = await fetch(
              `/api/content/pages-by-ids?ids=${encodeURIComponent(explicitTuneIds.join(','))}`,
              { cache: 'no-store' }
            ).then((r) => (r.ok ? r.json() : []));
            if (cancelled) return;
            tuneRows = pages || [];
          }
        } else if (tunesMode === 'tags') {
          const r = await fetch(
            `/api/content/tunes/related/${pageId}?limit=${tLim}`,
            { cache: 'no-store' }
          );
          const arr = r.ok ? await r.json() : [];
          if (cancelled) return;
          tuneRows = arr || [];
        } else if (tunesMode === 'both') {
          let explicit = [];
          if (explicitTuneIds.length) {
            const pages = await fetch(
              `/api/content/pages-by-ids?ids=${encodeURIComponent(explicitTuneIds.join(','))}`,
              { cache: 'no-store' }
            ).then((res) => (res.ok ? res.json() : []));
            explicit = pages || [];
          }
          const r = await fetch(
            `/api/content/tunes/related/${pageId}?limit=${tLim}`,
            { cache: 'no-store' }
          );
          const fuzzy = r.ok ? await r.json() : [];
          if (cancelled) return;
          const fuzzyList = fuzzy || [];
          const seen = new Set(explicit.map((x) => x.page_id));
          tuneRows = [
            ...explicit,
            ...fuzzyList.filter((t) => t.page_id && !seen.has(t.page_id)),
          ];
        } else {
          const r = await fetch(
            `/api/content/tunes/related/${pageId}?limit=${tLim}`,
            { cache: 'no-store' }
          );
          const arr = r.ok ? await r.json() : [];
          if (cancelled) return;
          tuneRows = arr || [];
        }

        if (cancelled) return;
        const siteOpts = await fetchTuneSiteOptions().catch(() => null);
        const minScore = siteOpts?.relatedTunesMinScore ?? DEFAULT_RELATED_TUNES_MIN_SCORE;
        tuneRows = filterRelatedTunesByScore(tuneRows, minScore);
        tuneRows = await enrichRelatedTuneRows(tuneRows);
        if (cancelled) return;
        setTunes(tuneRows);

        if (sect.curated === 'off') {
          setCurated([]);
        } else {
          const list = Array.isArray(cfg.curatedItems) ? cfg.curatedItems : [];
          const resolved = await resolveCuratedItemsForViewer(list, new Set([pageId]));
          if (!cancelled) setCurated(resolved);
        }
      } catch {
        if (!cancelled) {
          setProducts([]);
          setTunes([]);
          setArticles([]);
          setCurated([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pageId, relatedDataKey, pageData?.metadata]);

  const visibleProducts = products ? products.slice(0, productLimit) : null;
  const visibleTunes = tunes ? tunes.slice(0, tuneLimit) : null;
  const visibleArticles = articles ? articles.slice(0, articleLimit) : null;
  const visibleCurated = curated ? curated.slice(0, curatedLimit) : null;

  /* metadata footer items */
  const footerAttrs = [
    { label: 'Type',   val: tuneMeta.tuneType },
    { label: 'Key',    val: tuneMeta.keySignature },
    { label: 'Time',   val: tuneMeta.timeSig },
    { label: 'Region', val: tuneMeta.region },
    { label: 'Artist', val: tuneMeta.artist },
    { label: 'Album',  val: tuneMeta.album },
    { label: 'Book',   val: tuneMeta.book },
    { label: 'Alternate Titles', val: tuneMeta.alternateTitles, wide: true },
  ].filter(a => a.val);

  return (
    <div className="tune-page" data-page-id={pageId}>

      {/* ── Nav bar ── */}
      <div className="tune-nav-bar">
        <div className="tune-nav-left">
          {isAdmin && tuneMeta.sourceUrl && (
            <a
              href={tuneMeta.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="nav-link-original"
            >
              📎 View Original Page
            </a>
          )}
          {isAdmin && tuneMeta.ttaUrl && (
            <a
              href={tuneMeta.ttaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="nav-link-external"
            >
              🎶 Tune Archive
            </a>
          )}
          {isAdmin && tuneMeta.cbfiddleUrl && (
            <a
              href={tuneMeta.cbfiddleUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="nav-link-external"
            >
              🎻 CB Fiddle
            </a>
          )}
        </div>
        <button
          type="button"
          className="back-button"
          onClick={() => navigate(-1)}
          title="Back"
        >
          ←
        </button>
      </div>

      {/* ── Title ── */}
      <div className="tune-header">
        <h1 className="tune-title">{title}</h1>
      </div>

      {/* ── Notation images (asset_ids.images + legacy gif_asset_ids / static notation_image) ── */}
      {(tuneMeta.notationIds.length > 0 ||
        notationFallbackUrl) && (
        <div className="tune-notation">
          {tuneMeta.notationIds.map((id) => (
            <figure key={id} className="notation-asset-block notation-asset-block--image-only">
              <NotationAssetImg
                assetId={id}
                title={title}
                fallbackUrl={notationFallbackUrl || ''}
              />
            </figure>
          ))}
          {tuneMeta.notationIds.length === 0 && notationFallbackUrl ? (
            <figure className="notation-legacy-figure">
              <img
                src={notationFallbackUrl}
                alt={`${title} musical notation`}
                onError={(e) => {
                  const el = e.currentTarget.closest('.tune-notation');
                  if (el) el.style.display = 'none';
                }}
              />
            </figure>
          ) : null}
        </div>
      )}

      {/* ── Audio ── */}
      {tuneMeta.audioIds.length > 0 && (
        <div className="tune-audio">
          {tuneMeta.audioIds.map((id) => (
            <audio key={id} controls preload="metadata">
              {/* Omit type so the browser uses Content-Type from the download response (mp3/wav/etc.) */}
              <source src={`/api/assets/${id}/download`} />
              Your browser does not support the audio element.
            </audio>
          ))}
        </div>
      )}

      {/* ── About This Tune ── */}
      <div className="tune-description">
        <h2 className="tune-section-header">📝 About This Tune</h2>
        {footerAttrs.length > 0 && (
          <dl className="tune-meta-grid">
            {footerAttrs.map(({ label, val, wide }) => (
              <div
                key={label}
                className={`tune-meta-grid-item${wide ? ' tune-meta-grid-item--wide' : ''}`}
              >
                <dt>{label}</dt>
                <dd>{decodeHtmlEntities(String(val))}</dd>
              </div>
            ))}
          </dl>
        )}
        {/* description may contain HTML from legacy imports — sanitised by our own DB */}
        <div
          className="tune-description-text"
          dangerouslySetInnerHTML={{ __html: description }}
        />
        <p className="tune-fine-print">{FINE_PRINT}</p>
      </div>

      {/* ── Featured In (products) ── */}
      {visibleProducts !== null && visibleProducts.length > 0 && (
        <div className="related-products-section">
          <h2 className="tune-section-header">
            {sectionTitle('products', '📚 Featured In')}
          </h2>
          <div className="related-products-grid">
            {visibleProducts.map(p => (
              <ProductCard
                key={p.id}
                product={p}
                onClick={() => navigate(`/product/${p.id}`)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Related Articles ── */}
      {visibleArticles !== null && visibleArticles.length > 0 && (
        <div className="related-articles-section">
          <h2 className="tune-section-header">
            {sectionTitle('articles', '📰 Related Articles')}
          </h2>
          <div className="related-articles-list">
            {visibleArticles.map(a => (
              <ArticleCard
                key={a.page_id}
                article={a}
                onClick={() => navigate(`/page/${a.page_id}`)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Related Tunes ── */}
      {visibleTunes !== null && visibleTunes.length > 0 && (
        <div className="related-tunes">
          <h2 className="tune-section-header">
            {sectionTitle('tunes', '🎵 Related Tunes')}
          </h2>
          <div className="related-content-grid">
            {visibleTunes.map(t => (
              <TuneCard
                key={t.page_id}
                tune={t}
                onClick={() => navigate(`/page/${t.page_id}`)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Also See (curated) ── */}
      {visibleCurated !== null && visibleCurated.length > 0 && (
        <div className="curated-section">
          <h2 className="tune-section-header">
            {sectionTitle('curated', '🔗 Also See')}
          </h2>
          <div className="curated-grid">
            {visibleCurated.map((item, i) => (
              <CuratedCard
                key={
                  item.page_id ||
                  item.id ||
                  item.routeHref ||
                  item.tuneAssetId ||
                  i
                }
                item={item}
                onNavigate={(to) => navigate(to)}
              />
            ))}
          </div>
        </div>
      )}

      {pageId ? (
        <UserContentFeedback
          targetType="tune_page"
          targetId={String(pageId)}
          settingKey="user_ratings_tunes_enabled"
          title="Rate this tune"
        />
      ) : null}



    </div>
  );
}
